import { Injectable } from '@angular/core';
import { Observable, defer, throwError, timer } from 'rxjs';
import { catchError, map, retryWhen, mergeMap } from 'rxjs/operators';
import { HttpResponse } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { HttpService } from './http.service';

/* ===================== TRANSPORT MAP ===================== */
const TRANSPORT_METHOD_MAP: Record<string, number> = {
  'airplane_with_pressure': 1,
  'helicopter':             2,
  'lorry':                  3,
  'cable_car':              4,
};

/* ===================== INTERFACES ===================== */
export interface AirSimulationPayload {
  glazing: { glass: any };
  location: { longitude: number; latitude: number; altitude: number } | null;
  transport: { transportMethod: number; maxAltitudeDifference: number; startingAltitude: number } | null;
}

export interface AirSimulationCalculationResponse {
  lifetimeRes:  string;
  transportRes: string;
}

/* ===================== SERVICE ===================== */
@Injectable({ providedIn: 'root' })
export class AirSimulationApiService {

  private readonly apiUrl =
    environment.caluwin_api.base_url +
    environment.caluwin_api.endpoints.air_simulation_comprehensive;

  private readonly RETRY_COUNT   = 2;
  private readonly BASE_DELAY_MS = 200;

  constructor(
    private storageService: StorageService,
    private httpService: HttpService
  ) {}

  /* ===================== PUBLIC API ===================== */

  calculateAirSimulation(
    shortEdge: number,
    longEdge: number
  ): Observable<AirSimulationCalculationResponse> {
    return defer(async () => {
      const payload = await this.buildPayload(shortEdge, longEdge);
      console.log('Air simulation payload:', JSON.stringify(payload, null, 2));
      return this.httpService.executeWithAuthPolicy('POST', {
        url:     this.apiUrl,
        headers: this.getHeaders(),
        data:    payload,
      });
    }).pipe(
      map(this.handleSuccessResponse),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  /* ===================== PAYLOAD BUILDER ===================== */

  private async buildPayload(
    shortEdge: number,
    longEdge: number
  ): Promise<AirSimulationPayload> {
    const projectRaw = await this.storageService.getProject();
    const project    = projectRaw ? JSON.parse(projectRaw) : {};

    const glass    = project.windowglas        || {};
    const lifetime = project.lifetime          || {};
    const et       = project.elevatedTransport || {};

    const location = lifetime.latitude != null && lifetime.longitude != null
      ? { latitude: lifetime.latitude, longitude: lifetime.longitude, altitude: 0 }
      : null;

    const transport = et.transport
      ? {
          transportMethod:       TRANSPORT_METHOD_MAP[et.transport] ?? 1,
          maxAltitudeDifference: et.maxHeightDiff ?? 0,
          startingAltitude:      0
        }
      : null;

    return {
      glazing: {
        glass: {
          id:                     glass.id                     ?? '',
          glazingCount:           glass.glazingCount           ?? 0,
          ug:                     glass.ug                     ?? 0,
          interiorGlassThickness: glass.interiorGlassThickness ?? 0,
          exteriorGlassThickness: glass.exteriorGlassThickness ?? 0,
          middleGlassThickness:   glass.middleGlassThickness   ?? 0,
          cavities:               glass.cavities               ?? [],
          name:                   glass.name                   ?? '',
          type:                   glass.type                   ?? '',
          thickness:              glass.thickness              ?? 0,
          category:               glass.category               ?? '',
          uValue:                 glass.ug                     ?? 0,
          gValue:                 glass.gValue                 ?? 0,
          lightTransmittance:     glass.lightTransmittance     ?? 0,
          coating:                glass.coating                ?? '',
          manufacturer:           glass.manufacturer           ?? 0,
          isLowE:                 glass.isLowE                 ?? false,
          region:                 glass.region                 ?? [],
          structure:              glass.structure              ?? '',
          shortEdge,
          longEdge,
        }
      },
      location,
      transport,
    };
  }

  /* ===================== HELPERS ===================== */

  private getHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  private handleSuccessResponse(response: HttpResponse): AirSimulationCalculationResponse {
    if (response.status < 200 || response.status >= 300) throw response;
    return response.data as AirSimulationCalculationResponse;
  }

  /* ===================== RETRY ===================== */

  private retryWithBackoff() {
    return (errors: Observable<any>) =>
      errors.pipe(
        mergeMap((error, retryIndex) => {
          const attempt = retryIndex + 1;
          if (attempt > this.RETRY_COUNT || !this.isRetryableError(error?.status)) {
            return throwError(() => error);
          }
          return timer(Math.pow(2, attempt) * this.BASE_DELAY_MS);
        })
      );
  }

  private isRetryableError(status?: number): boolean {
    return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
  }

  /* ===================== ERROR HANDLING ===================== */

  private handleFinalError(error: any): Error {
    if (typeof error?.status === 'number') return this.handleHttpError(error.status, error.data);
    return this.handleNetworkError(error);
  }

  private handleHttpError(status: number, data: any): Error {
    const messageMap: Record<number, string> = {
      400: 'Missing parameters.',
      401: 'Your session expired. Please sign in again.',
      403: 'Access forbidden.',
      404: 'API endpoint not found.',
      422: 'Validation error. Please check your inputs.',
      429: 'Too many requests. Please try again later.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable.',
      503: 'Service temporarily unavailable.',
      504: 'Service temporarily unavailable.',
    };
    const message = data?.message || messageMap[status] || `Error ${status}`;
    console.error('API Error:', status, data);
    return new Error(message);
  }

  private handleNetworkError(error: any): Error {
    const message = error?.message?.includes('timeout')
      ? 'Request timeout. Please try again.'
      : 'Network error. Please check your connection.';
    console.error('Network Error:', error);
    return new Error(message);
  }
}
